import { useMemo } from 'react'
import { useGetReceiptVaultUploadRulesQuery } from '../../../services/authApi'
import {
  formatReceiptVaultRuleForTooltip,
  ReceiptVaultRulesPayload,
} from './receiptVaultRuleUtils'

export type { ReceiptVaultRulesPayload, ReceiptVaultRule } from './receiptVaultRuleUtils'

const defaultRules: ReceiptVaultRulesPayload = {
  title: 'Receipt Upload Rules',
  intro: 'Please review these rules before attaching a receipt or document.',
  rules: [
    'Upload clear, complete receipts that show merchant name, date, and total amount.',
    'Accepted formats: PNG, JPG, JPEG, and PDF.',
    'Do not upload unrelated personal documents or blank/unreadable images.',
    'Ensure the receipt matches the expense amount, date, and vendor when possible.',
    'You are responsible for ensuring attached receipts are accurate and business-related.',
  ],
}

export function useReceiptVaultRules(enabled = true) {
  const { data, isFetching, isError } = useGetReceiptVaultUploadRulesQuery(undefined, {
    skip: !enabled,
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  })

  const displayOnUploadPopup = !!data?.result?.displayOnUploadPopup

  const rulesObj: ReceiptVaultRulesPayload = useMemo(() => {
    const raw = data?.result?.rules
    if (raw && typeof raw === 'object') return raw as ReceiptVaultRulesPayload
    try {
      if (data?.result?.rulesJson) return JSON.parse(data.result.rulesJson) as ReceiptVaultRulesPayload
    } catch {
      /* ignore */
    }
    return defaultRules
  }, [data])

  const tooltipText = useMemo(() => {
    const lines: string[] = []
    if (rulesObj.intro) lines.push(rulesObj.intro)
    if (rulesObj.rules?.length) {
      if (lines.length) lines.push('')
      rulesObj.rules.forEach((rule) => lines.push(`• ${formatReceiptVaultRuleForTooltip(rule)}`))
    }
    return lines.join('\n') || defaultRules.intro || ''
  }, [rulesObj])

  return {
    rulesObj,
    displayOnUploadPopup,
    isFetching,
    isError,
    tooltipText,
  }
}
